import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../../utils/api'
import { toast } from 'react-toastify';
import AdminLayout from '../components/AdminLayout';

interface Metric {
  id?: number;
  name: string;
  endpoint: string;
  active: boolean;
  timelineAvailable: boolean;
  benchmarkAvailable: boolean;
  withComparison: boolean;
  group: string;
  permissionCategory: string;
  permissionCapability: string;
  policyFeature: string;
  units: string;
}

const FormSection = styled.div`
  background: white;
  padding: 0;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 16px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
  &:focus { outline: none; border-color: #85b7d9; }
  &:disabled { background: #f3f4f6; color: #6b7280; }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-radius: 4px;
  font-size: 14px;
  background: white;
  box-sizing: border-box;
  &:focus { outline: none; border-color: #85b7d9; }
`;

const CheckboxGroup = styled.div`
  margin-bottom: 12px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  input { margin-top: 2px; }
`;

const HelperText = styled.div`
  font-size: 12px;
  color: #00a6c2;
  margin-top: 2px;
  margin-left: 22px;
`;

const ThreeColumnRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px;
  margin-bottom: 16px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const CancelButton = styled(Link)`
  padding: 10px 20px;
  background: #e0e0e0;
  color: #333;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  text-decoration: none;
  &:hover { background: #d0d0d0; color: #333; }
`;

const SaveButton = styled.button`
  padding: 10px 20px;
  background: #2185d0;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  &:hover { background: #1678c2; }
  &:disabled { background: #9ca3af; cursor: not-allowed; }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 48px;
  color: #6b7280;
`;

function AdminMetricEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id;
  
  const [metric, setMetric] = useState<Metric>({
    name: '',
    endpoint: '',
    active: false,
    timelineAvailable: false,
    benchmarkAvailable: false,
    withComparison: true,
    group: '',
    permissionCategory: '',
    permissionCapability: '',
    policyFeature: '',
    units: ''
  });
  const [groups, setGroups] = useState<string[]>([]);
  const [policyFeatures, setPolicyFeatures] = useState<string[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  const fetchMetric = useCallback(async () => {
    if (isNew) return;
    
    try {
      const res = await api.get(`/admin2/metrics/${id}.json`);
      setMetric(res.data.metric);
      setGroups(res.data.groups || []);
      setPolicyFeatures(res.data.policyFeatures || []);
    } catch (err) {
      console.error('Failed to load metric', err);
      toast.error('Failed to load metric');
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  const fetchOptions = useCallback(async () => {
    if (!isNew) return;
    
    try {
      const res = await api.get('/admin2/metrics/new.json');
      setGroups(res.data.groups || []);
      setPolicyFeatures(res.data.policyFeatures || []);
    } catch (err) {
      console.error('Failed to load options', err);
    } finally {
      setLoading(false);
    }
  }, [isNew]);

  useEffect(() => {
    if (isNew) {
      fetchOptions();
    } else {
      fetchMetric();
    }
  }, [fetchMetric, fetchOptions, isNew]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        metric: {
          name: metric.name,
          endpoint: metric.endpoint,
          active: metric.active,
          timeline_available: metric.timelineAvailable,
          benchmark_available: metric.benchmarkAvailable,
          with_comparison: metric.withComparison,
          group: metric.group,
          permission_category: metric.permissionCategory,
          permission_capability: metric.permissionCapability,
          policy_feature: metric.policyFeature,
          units: metric.units
        }
      };

      if (isNew) {
        const res = await api.post('/admin2/metrics.json', payload);
        toast.success('Metric created successfully');
        navigate(`/admin2/metrics/${res.data.metric.id}/edit`);
      } else {
        await api.put(`/admin2/metrics/${id}.json`, payload);
        toast.success('Metric updated successfully');
      }
    } catch (err: any) {
      const errors = err.response?.data?.errors;
      toast.error(errors ? errors.join(', ') : 'Failed to save metric');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title={isNew ? 'Create a metric' : 'Edit metric'} pageTitle={isNew ? 'Metrics / New' : 'Metrics / Edit'}>
        <LoadingState>Loading...</LoadingState>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={isNew ? 'Create a metric' : 'Edit metric'} pageTitle={isNew ? 'Metrics / New' : `Metrics / ${metric.name}`}>
      <FormSection>
        <form onSubmit={handleSubmit}>
          <FormRow>
            <FormGroup>
              <Label>Name</Label>
              <Input
                type="text"
                value={metric.name}
                onChange={(e) => setMetric({ ...metric, name: e.target.value })}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Endpoint</Label>
              <Input
                type="text"
                value={metric.endpoint}
                onChange={(e) => setMetric({ ...metric, endpoint: e.target.value })}
                disabled={!isNew}
                required
              />
            </FormGroup>
          </FormRow>

          <CheckboxGroup>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={metric.active}
                onChange={(e) => setMetric({ ...metric, active: e.target.checked })}
              />
              Active
            </CheckboxLabel>
          </CheckboxGroup>

          <CheckboxGroup>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={metric.timelineAvailable}
                onChange={(e) => setMetric({ ...metric, timelineAvailable: e.target.checked })}
              />
              Timeline available
            </CheckboxLabel>
          </CheckboxGroup>

          <CheckboxGroup>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={metric.benchmarkAvailable}
                onChange={(e) => setMetric({ ...metric, benchmarkAvailable: e.target.checked })}
              />
              Benchmark available
            </CheckboxLabel>
          </CheckboxGroup>

          <CheckboxGroup>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={metric.withComparison}
                onChange={(e) => setMetric({ ...metric, withComparison: e.target.checked })}
              />
              With comparison
            </CheckboxLabel>
            <HelperText>Does this metric include prior information for comparison</HelperText>
          </CheckboxGroup>

          <ThreeColumnRow>
            <FormGroup>
              <Label>Policy feature</Label>
              <Select
                value={metric.policyFeature}
                onChange={(e) => setMetric({ ...metric, policyFeature: e.target.value })}
              >
                <option value="">Select...</option>
                {policyFeatures.map((pf) => (
                  <option key={pf} value={pf}>{pf}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>Permission category</Label>
              <Input
                type="text"
                value={metric.permissionCategory}
                onChange={(e) => setMetric({ ...metric, permissionCategory: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <Label>Permission capability</Label>
              <Input
                type="text"
                value={metric.permissionCapability}
                onChange={(e) => setMetric({ ...metric, permissionCapability: e.target.value })}
              />
            </FormGroup>
          </ThreeColumnRow>

          <FormGroup>
            <Label>Group</Label>
            <Select
              value={metric.group}
              onChange={(e) => setMetric({ ...metric, group: e.target.value })}
              required
            >
              <option value="">Select a group</option>
              {groups.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Units</Label>
            <Input
              type="text"
              value={metric.units}
              onChange={(e) => setMetric({ ...metric, units: e.target.value })}
              required
            />
          </FormGroup>

          <ButtonRow>
            <CancelButton to="/admin2/metrics">Cancel</CancelButton>
            <SaveButton type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </SaveButton>
          </ButtonRow>
        </form>
      </FormSection>
    </AdminLayout>
  );
}

export default AdminMetricEditPage;
